import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { urlParam, useUrlState } from "@/shared/hooks/useUrlState";
import { useRollLayouts } from "../hooks/useRollLayouts";
import { buildDocumentUrl } from "../models/RollLayout";
import { DocumentsList } from "./DocumentsList";
import { PdfViewer } from "./PdfViewer";

// Stand selector and the Documents panel share this width so they line up.
const DOCUMENTS_PANEL_WIDTH = 300;

// Legacy uses a fixed Stand 1-17 selector (the @StandId the SP takes), not the
// stand catalog — so the dropdown stays short and matches the legacy navbar.
const STAND_OPTIONS = Array.from({ length: 17 }, (_, i) => ({
	number: i + 1,
	label: `Stand ${i + 1}`,
}));

const BASE_PATH = "/reports/roll-layouts";

const URL_SCHEMA = {
	// The stand stays in the path, where the navbar links point. The document is a
	// selection, and its path is what identifies it.
	doc: urlParam.string(),
};

const clampStand = (value: string | undefined): number => {
	const n = Number(value);
	return Number.isInteger(n) && n >= 1 && n <= 17 ? n : 1;
};

export const RollLayouts = () => {
	const { standId } = useParams();
	const navigate = useNavigate();
	const [selectedStand, setSelectedStand] = useState(() => clampStand(standId));
	const [{ doc }, setFilters, { dropValues }] = useUrlState(URL_SCHEMA);

	// Re-sync when the navbar deep-links to a different stand (/reports/roll-layouts/:standId).
	useEffect(() => {
		setSelectedStand(clampStand(standId));
	}, [standId]);

	const {
		data: documents = [],
		isLoading,
		isError,
		isSuccess,
	} = useRollLayouts({ standNumber: selectedStand });

	const selectedDoc = useMemo(
		() => documents.find((d) => d.DocumentPath === doc) ?? null,
		[documents, doc],
	);

	// A link to a document this stand does not hold would leave the viewer blank.
	useEffect(() => {
		if (!isSuccess || doc === null) return;
		if (!documents.some((d) => d.DocumentPath === doc)) dropValues("doc");
	}, [isSuccess, documents, doc, dropValues]);

	// Until now the selector changed the stand without touching the URL, so the screen
	// could not be linked. Replace, so Back leaves the page.
	const handleStandChange = (value: number) => {
		setSelectedStand(value);
		navigate(
			{ pathname: `${BASE_PATH}/${value}`, search: "" },
			{ replace: true },
		);
	};

	const selectedUrl = selectedDoc
		? buildDocumentUrl(selectedDoc.DocumentPath)
		: null;

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Roll Layouts
			</Typography>

			<Paper elevation={3}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						px: 1.5,
						py: 0.75,
						flexWrap: "wrap",
					}}
				>
					<Box sx={{ width: 220 }}>
						<TextField
							select
							fullWidth
							size="small"
							label="Stand"
							value={selectedStand}
							onChange={(e) => handleStandChange(Number(e.target.value))}
						>
							{STAND_OPTIONS.map((option) => (
								<MenuItem key={option.number} value={option.number}>
									{option.label}
								</MenuItem>
							))}
						</TextField>
					</Box>
					<ResetFiltersButton
						onClick={() => handleStandChange(1)}
						disabled={selectedStand === 1 && doc === null}
					/>
				</Box>
			</Paper>

			<Box sx={{ display: "flex", gap: 1, height: "calc(100vh - 210px)" }}>
				<Paper
					elevation={3}
					sx={{
						width: DOCUMENTS_PANEL_WIDTH,
						flexShrink: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
						<Typography
							variant="subtitle2"
							sx={{
								textTransform: "uppercase",
								letterSpacing: 0.5,
								color: "text.secondary",
							}}
						>
							Documents
						</Typography>
					</Box>
					<Box sx={{ flex: 1, overflowY: "auto" }}>
						<DocumentsList
							documents={documents}
							isLoading={isLoading}
							isError={isError}
							selectedPath={selectedDoc?.DocumentPath ?? null}
							onSelect={(document) =>
								setFilters({ doc: document.DocumentPath })
							}
						/>
					</Box>
				</Paper>

				<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
					<PdfViewer url={selectedUrl} title={selectedDoc?.DocumentName} />
				</Paper>
			</Box>
		</Box>
	);
};
